import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/components/auth/AuthProvider';
import { ArrowRight, Search, BookmarkCheck, History } from 'lucide-react';
import { Hero } from '@/components/layout/Hero';
import { FAQ } from '@/components/faq/FAQ';
import { FeatureGrid } from '@/components/hero/FeatureGrid';
import { GmailSection } from '@/components/hero/GmailSection';
import { motion } from '@/components/ui/motion-provider';
import { SplineScene } from "@/components/ui/splite";
import { Spotlight } from "@/components/ui/spotlight";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  linkTo: string;
  linkText: string;
}

function FeatureCard({ icon, title, description, linkTo, linkText }: FeatureCardProps) {
  return (
    <Card className="bg-white dark:bg-[#111] text-gray-900 dark:text-white border border-gray-100 dark:border-0 p-6 space-y-4 hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors rounded-[1.25rem] shadow-sm hover:shadow-md">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white">
          {icon}
        </div>
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
      <p className="text-gray-600 dark:text-gray-400 text-sm">
        {description}
      </p>
      <Link 
        to={linkTo}
        className="inline-flex items-center text-sm text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 transition-colors group rounded-lg"
      >
        {linkText}
        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </Card>
  );
}

export default function Home() {
  const { user } = useAuth();
  
  const firstName = user?.user_metadata?.name?.split(' ')[0] || 
    user?.user_metadata?.user_name || 
    user?.user_metadata?.preferred_username ||
    user?.user_metadata?.github_username ||
    user?.user_metadata?.username ||
    user?.email?.split('@')[0] ||
    'Developer';

  if (!user) {
    return (
      <div className="flex flex-col bg-background">
        <section className="relative rounded-b-[1.5rem] sm:rounded-b-[2rem]">
          <div className="relative pt-2 sm:pt-4 pb-8 sm:pb-12">
            <Hero />
          </div>
        </section>

        <FeatureGrid />

        <GmailSection />

        <section className="w-full py-8 sm:py-16 md:py-24 rounded-[1.5rem] sm:rounded-[2rem]">
          <div className="container px-3 sm:px-4 md:px-6">
            <FAQ />
          </div>
        </section>
      </div>
    );
  }

  const features: FeatureCardProps[] = [
    {
      icon: <Search className="w-5 h-5" />,
      title: "Search Developers",
      description: "Find developers by location, tech stack, experience level, and more. Get detailed insights into their work and contributions.",
      linkTo: "/search",
      linkText: "Start Searching"
    },
    {
      icon: <BookmarkCheck className="w-5 h-5" />,
      title: "Saved Profiles",
      description: "Keep track of interesting developers. Create custom lists and add notes to organize your talent pool effectively.",
      linkTo: "/saved-profiles",
      linkText: "View Saved Profiles"
    },
    {
      icon: <History className="w-5 h-5" />,
      title: "Saved Searches",
      description: "Save and reuse your search filters. Get notified when new developers match your saved search criteria.",
      linkTo: "/saved-searches",
      linkText: "View Searches"
    }
  ];

  return (
    <div className="container max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 space-y-6 sm:space-y-8 dark:text-gray-100 text-gray-900">
      <Card className="relative overflow-hidden border-0 rounded-[1.25rem] sm:rounded-[1.5rem] bg-gradient-to-br from-gray-50 to-white dark:from-black dark:to-gray-900 shadow-xl">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        
        <Spotlight
          className="-top-40 left-0 md:left-60 md:-top-20"
        />
        
        <div className="relative flex flex-col md:flex-row justify-between p-6 sm:p-8 md:p-10 gap-6 sm:gap-8">
          <motion.div 
            className="space-y-4 sm:space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="space-y-2">
              <motion.h1 
                className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-primary dark:text-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                Welcome back, {firstName}!
              </motion.h1>
              <motion.p 
                className="text-gray-600 dark:text-gray-100 text-sm sm:text-base md:text-lg max-w-xl dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                Ready to discover exceptional developers? Use our powerful tools to find and connect with the perfect talent for your team.
              </motion.p>
            </div>

            <motion.div 
              className="flex flex-wrap gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Link 
                to="/search" 
                className="inline-flex items-center bg-primary/90 hover:bg-primary text-primary-foreground px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-200 group hover:translate-y-[-1px] text-sm sm:text-base"
              >
                Start Searching
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div 
            className="hidden md:flex items-center justify-center relative w-[250px] lg:w-[300px] xl:w-[350px]"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <SplineScene 
              scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode"
              className="w-full h-full object-contain scale-[1.5] lg:scale-[1.75] origin-center transform-gpu cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 blur-3xl animate-pulse pointer-events-none" />
          </motion.div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
          >
            <FeatureCard {...feature} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
